import { Suspense, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { Iconmark } from 'src/shared/components/icons/logos/iconmark'
import { Logo } from 'src/shared/components/icons/logos/logo'
import { Button } from 'src/shared/components/ui/buttons/button'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'
import Sidebar from 'src/shared/features/home/components/sidebar'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { useWindowSize } from 'src/shared/hooks/use-window-size'
import { cn } from 'src/shared/utils/cn'

import { useUserByUsername } from '../../../api/accounts/use-user-by-username'
import { useUserReviews } from '../../../api/marketplace/use-user-reviews'
import { ReviewStar } from '../../../assets/review-star'
import { professionMap } from '../../../schemas/profession'
import { useMobilePublicProfileStore } from '../../../stores/mobile-public-profile-store'
import AuthModal from '../../marketplace/modals/auth'
import { ProfilePhotoFallback } from '../../profile-photos/profile-avatar'

import RequestCallButton from './request-call-btn'

const MobileUserRecapContent = () => {
  const { username } = useParams<{ username: string }>()
  const { data: user } = useUserByUsername(username || '')
  const { data: reviews } = useUserReviews(user?.id || -1)
  const averageRating = reviews?.averageRating || 0

  const ratingSpan =
    reviews && reviews?.totalReviews && reviews?.totalReviews > 0 ? (
      <span className="text-sm">
        <ReviewStar className="mb-[3px] inline size-[14px]" /> {averageRating.toFixed(1)}
      </span>
    ) : null

  if (!user) {
    return null
  }

  return (
    <div className="flex w-max flex-row items-center gap-2">
      <div
        className="size-[40px] min-h-[40px] min-w-[40px] rounded-full border-[3px] border-white bg-white"
        style={{
          boxShadow: '0px 1px 12px 1px rgba(27, 48, 41, 0.10)',
        }}
      >
        <img src={user.profilePhoto || ProfilePhotoFallback} alt={user.firstName} className="size-full rounded-full" />
      </div>
      <div className="flex flex-col items-start justify-center gap-0.5">
        <p className="text-[15px] font-bold leading-none">
          {user.fullName} {ratingSpan}
        </p>
        <p className="text-xs leading-none text-muted">{professionMap[user.profession].label}</p>
      </div>
    </div>
  )
}

const MobileUserRecap = () => (
  <Suspense fallback={null}>
    <MobileUserRecapContent />
  </Suspense>
)

const PublicProfileHeader = () => {
  const isMobile = useIsMobile()
  const { width } = useWindowSize()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { username } = useParams<{ username: string }>()
  const [showModal, setShowModal] = useState(false)
  const [authModalFlow, setAuthModalFlow] = useState<'signup' | 'login'>('login')
  const { isSignedIn, isConsumerSignedIn } = useAuthState()
  const { isDividerScrolledPast, isTabsScrolledPast } = useMobilePublicProfileStore()

  const handleClientLoginClick = (signup = false) => {
    setShowModal(true)
    setAuthModalFlow(signup ? 'signup' : 'login')
  }

  return (
    <header
      className={cn(
        'fixed top-0 z-50 flex h-[70px] w-full items-center justify-between bg-accent p-4 transition-all duration-300 md:h-[100px] md:px-8',
        isDividerScrolledPast && 'bg-white',
      )}
      style={
        isTabsScrolledPast
          ? {
              borderBottom: '0.5px solid white',
            }
          : {
              borderBottom: '0.5px solid rgba(216, 216, 214, 0.75)',
              boxShadow: '0px 4px 12px 0px rgba(27, 48, 41, 0.03)',
            }
      }
    >
      <div
        className={cn(
          'mx-auto flex size-full max-w-[calc(2400px-4rem)] items-center justify-between',
          'max-w-[calc(1400px-6rem)]',
        )}
      >
        <div className="relative flex h-full flex-row items-center justify-center gap-4">
          <div
            className={cn(
              'absolute bottom-0 left-0 top-0 -translate-x-[calc(100%+30px)] opacity-0 transition-all duration-300',
              isDividerScrolledPast && width < 768 && 'translate-x-0 opacity-100',
            )}
          >
            <MobileUserRecap />
          </div>
          <div
            className={cn(
              'absolute bottom-0 left-0 top-0 translate-x-0 opacity-100 transition-all duration-300',
              isDividerScrolledPast && width < 768 && '-translate-x-[calc(100%+30px)] opacity-0',
            )}
          >
            <Link to={paths.root.getHref()} className="flex h-full flex-col items-start justify-center gap-2">
              {width < 768 ? (
                <Iconmark className="size-[30px] md:size-[32px]" />
              ) : (
                <Logo className="h-[22px] w-[115px] md:h-[30px] md:w-[155px]" />
              )}
            </Link>
          </div>
        </div>
        <div className="flex flex-row items-center">
          {!isMobile && !isSignedIn && !isConsumerSignedIn && (
            <Button variant="outline" size="sm" className="mr-2 border-none" onClick={() => handleClientLoginClick()}>
              <p className="text-sm font-bold tracking-wide">Login</p>
            </Button>
          )}
          <RequestCallButton
            username={username as string}
            textClassName="text-[14px] md:text-[15px]"
            className="h-[41px]"
            text={isDividerScrolledPast ? 'Call now' : 'Request call now'}
          />
        </div>
      </div>
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <AuthModal flow={authModalFlow} open={showModal} onClose={() => setShowModal(false)} />
    </header>
  )
}

export default PublicProfileHeader
